'use client';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/core/utils/cn';

interface AnimatedBlockDivProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tailwind class from getAnimationClass(), e.g. "animate-fade-in". */
  animationClass?: string;
  /** Off in the admin builder canvas so editors see blocks immediately while arranging them. */
  animateOnScroll?: boolean;
  /** Scroll-linked motion (rotate/translate while the block crosses the viewport). */
  scrollFx?: ScrollFx;
}

export interface ScrollFx {
  /** Total rotation in degrees across the pass; 0° when the block is centred in the viewport. */
  rotate?: number;
  /** Total horizontal / vertical travel in px across the pass. */
  x?: number;
  y?: number;
  /** Static "Obrót (kolaż)" rotation the scroll rotation is added to. */
  baseRotate?: number;
  /** Phones (<640px) keep the block still. */
  skipMobile?: boolean;
}

export function getScrollFx(styles: any): ScrollFx | undefined {
  const rotate = Number(styles?.scrollRotate) || 0;
  const x = Number(styles?.scrollX) || 0;
  const y = Number(styles?.scrollY) || 0;
  if (!rotate && !x && !y) return undefined;
  return { rotate, x, y, baseRotate: Number(styles?.rotate) || 0, skipMobile: Boolean(styles?.scrollFxSkipMobile) };
}

/**
 * Drives --sfx-* custom properties from the block's position in the viewport. Progress runs
 * 0 → 1 while the block travels from the bottom edge to the top edge; values are centred so the
 * block sits at its designed position when it is in the middle of the screen. Measured from the
 * untransformed layout box, so the motion itself never feeds back into the measurement.
 */
function useScrollFx(ref: React.MutableRefObject<HTMLDivElement | null>, fx?: ScrollFx) {
  const rotate = fx?.rotate || 0;
  const x = fx?.x || 0;
  const y = fx?.y || 0;
  const skipMobile = Boolean(fx?.skipMobile);
  useEffect(() => {
    const node = ref.current;
    if (!node || (!rotate && !x && !y) || typeof window === 'undefined') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia?.('(max-width: 639px)');
    let frame = 0;
    let visible = true;
    let currentY = 0;
    const update = () => {
      frame = 0;
      if (reduced?.matches || (skipMobile && mobile?.matches)) {
        node.style.removeProperty('--sfx-r');
        node.style.removeProperty('--sfx-x');
        node.style.removeProperty('--sfx-y');
        currentY = 0;
        return;
      }
      const rect = node.getBoundingClientRect();
      const height = node.offsetHeight || rect.height;
      const top = rect.top + rect.height / 2 - currentY - height / 2;
      const vh = window.innerHeight || 1;
      const progress = Math.min(1, Math.max(0, (vh - top) / (vh + height)));
      const centred = progress - 0.5;
      currentY = centred * y;
      node.style.setProperty('--sfx-r', `${(centred * rotate).toFixed(2)}deg`);
      node.style.setProperty('--sfx-x', `${(centred * x).toFixed(1)}px`);
      node.style.setProperty('--sfx-y', `${currentY.toFixed(1)}px`);
    };
    const schedule = () => {
      if (visible && !frame) frame = requestAnimationFrame(update);
    };
    const observer = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); }, { rootMargin: '100px 0px' })
      : null;
    observer?.observe(node);
    // Capture phase: also catches scrolling inside the builder canvas, not just the window.
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    update();
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, rotate, x, y, skipMobile]);
}

/**
 * Drop-in replacement for a plain block wrapper <div> that defers the
 * animate-* class (see blockHelpers.getAnimationClass) until the block
 * scrolls into the viewport, instead of firing the moment it mounts —
 * previously every block on the page animated in simultaneously on load
 * regardless of scroll position, since the CSS animation classes were
 * applied unconditionally at mount.
 */
export const AnimatedBlockDiv = React.forwardRef<HTMLDivElement, AnimatedBlockDivProps>(
  ({ animationClass, animateOnScroll = true, scrollFx, className, children, style, ...rest }, forwardedRef) => {
    const localRef = useRef<HTMLDivElement | null>(null);
    useScrollFx(localRef, scrollFx);
    const shouldDefer = animateOnScroll && !!animationClass;
    const [inView, setInView] = useState(!shouldDefer);

    useEffect(() => {
      if (!shouldDefer || inView) return;
      const node = localRef.current;
      if (!node || typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }
      if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        setInView(true);
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [shouldDefer, inView]);

    return (
      <div
        ref={(node) => {
          localRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={cn(className, inView ? animationClass : shouldDefer && 'opacity-0')}
        style={scrollFx ? {
          ...style,
          // Individual transform properties compose with the entrance/hover `transform`s.
          rotate: `calc(${scrollFx.baseRotate || 0}deg + var(--sfx-r, 0deg))`,
          translate: 'var(--sfx-x, 0px) var(--sfx-y, 0px)',
        } : style}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
AnimatedBlockDiv.displayName = 'AnimatedBlockDiv';
